import * as React from 'react';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AppNavbar from './AppNavbar';
import SideMenu from './SideMenu';
import AppTheme from '../../shared-theme/AppTheme';
import { useGrow, GROW_STAGES } from '../context/GrowContext';
import { SENSOR_TYPES } from '../context/SensorConfigContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Sensor groups for organized display
const SENSOR_GROUPS = [
  {
    label: 'Water Reservoir',
    sensors: ['ph', 'ec', 'tds', 'waterTemp', 'orp'],
  },
  {
    label: 'Atmosphere',
    sensors: ['airTemp', 'humidity', 'co2', 'vpd'],
  },
  {
    label: 'Lighting',
    sensors: ['ppfd', 'dli'],
  },
];

// Stage tabs
const STAGE_TABS = [
  { id: 'seedling', label: 'Seedling/Clone' },
  { id: 'vegetative', label: 'Vegetative' },
  { id: 'flower', label: 'Flower' },
  { id: 'lateFlower', label: 'Mid-Late Flower' },
];

function SensorRangeEditor({ sensorType, optimalMin, optimalMax, onChange }) {
  const config = SENSOR_TYPES[sensorType];
  if (!config) return null;

  const handleOptimalMinChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      onChange(value, optimalMax);
    }
  };

  const handleOptimalMaxChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      onChange(optimalMin, value);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
          {config.label}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
          {config.unit}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Sensor Min (read-only) */}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', display: 'block', mb: 0.5 }}>
            Min
          </Typography>
          <TextField
            value={config.min}
            size="small"
            disabled
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                py: 0.75,
              },
              '& .MuiInputBase-root': {
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
        {/* Optimal Min */}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'primary.main', fontFamily: 'Inter, sans-serif', display: 'block', mb: 0.5, fontWeight: 600 }}>
            Opt Min
          </Typography>
          <TextField
            type="number"
            value={optimalMin}
            onChange={handleOptimalMinChange}
            size="small"
            inputProps={{
              step: config.max > 100 ? 10 : config.max > 10 ? 0.5 : 0.1,
              min: config.min,
              max: optimalMax,
            }}
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                py: 0.75,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>
        {/* Optimal Max */}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'secondary.main', fontFamily: 'Inter, sans-serif', display: 'block', mb: 0.5, fontWeight: 600 }}>
            Opt Max
          </Typography>
          <TextField
            type="number"
            value={optimalMax}
            onChange={handleOptimalMaxChange}
            size="small"
            inputProps={{
              step: config.max > 100 ? 10 : config.max > 10 ? 0.5 : 0.1,
              min: optimalMin,
              max: config.max,
            }}
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                py: 0.75,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'secondary.main',
                },
              },
            }}
          />
        </Box>
        {/* Sensor Max (read-only) */}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', display: 'block', mb: 0.5 }}>
            Max
          </Typography>
          <TextField
            value={config.max}
            size="small"
            disabled
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                py: 0.75,
              },
              '& .MuiInputBase-root': {
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

function PresetEditor({ preset, onUpdate, onCopyStage, onPasteStage, copiedStage }) {
  const [activeStage, setActiveStage] = React.useState('seedling');
  const { updateSensorRange, updateStageSettings } = useGrow();

  if (!preset) return null;

  const stageConfig = preset.stages[activeStage];
  if (!stageConfig) return null;

  const handleNameChange = (e) => {
    onUpdate(preset.id, { name: e.target.value });
  };

  const handleDescriptionChange = (e) => {
    onUpdate(preset.id, { description: e.target.value });
  };

  const handleDurationChange = (e) => {
    const duration = parseInt(e.target.value, 10) || 0;
    updateStageSettings(preset.id, activeStage, { duration });
  };

  const handleSensorChange = (sensorType, optimalMin, optimalMax) => {
    updateSensorRange(preset.id, activeStage, sensorType, optimalMin, optimalMax);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Preset header */}
      <Box sx={{ mb: 2 }}>
        <TextField
          value={preset.name}
          onChange={handleNameChange}
          variant="standard"
          fullWidth
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '1.25rem',
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
        <TextField
          value={preset.description || ''}
          onChange={handleDescriptionChange}
          placeholder="Add a description..."
          variant="standard"
          fullWidth
          size="small"
          sx={{
            mt: 1,
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
              color: 'text.secondary',
            },
          }}
        />
      </Box>

      {/* Stage tabs */}
      <Tabs
        value={activeStage}
        onChange={(_, v) => setActiveStage(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {STAGE_TABS.map((stage) => (
          <Tab
            key={stage.id}
            value={stage.id}
            label={stage.label}
            sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
          />
        ))}
      </Tabs>

      {/* Stage controls */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          label="Duration (days)"
          type="number"
          value={stageConfig.duration}
          onChange={handleDurationChange}
          size="small"
          sx={{ width: 120 }}
          inputProps={{ min: 1, max: 365 }}
        />
        <Tooltip title="Copy stage settings">
          <IconButton
            size="small"
            onClick={() => onCopyStage(activeStage)}
            sx={{ bgcolor: 'action.hover' }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={copiedStage ? `Paste from ${GROW_STAGES[copiedStage]?.label}` : 'No stage copied'}>
          <span>
            <IconButton
              size="small"
              onClick={() => onPasteStage(activeStage)}
              disabled={!copiedStage}
              sx={{ bgcolor: 'action.hover' }}
            >
              <ContentPasteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Sensor ranges */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {SENSOR_GROUPS.map((group) => (
          <Box key={group.label} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: 'text.secondary',
                mb: 1.5,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: 1,
              }}
            >
              {group.label}
            </Typography>
            {group.sensors.map((sensorType) => {
              const sensorRange = stageConfig.sensors[sensorType];
              if (!sensorRange) return null;
              return (
                <SensorRangeEditor
                  key={sensorType}
                  sensorType={sensorType}
                  optimalMin={sensorRange.optimalMin}
                  optimalMax={sensorRange.optimalMax}
                  onChange={(min, max) => handleSensorChange(sensorType, min, max)}
                />
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function PresetList({ presets, selectedId, onSelect, onAdd, onDelete, onDuplicate }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
          Presets
        </Typography>
        <Tooltip title="Add new preset">
          <IconButton size="small" onClick={onAdd}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <List dense sx={{ flex: 1, overflow: 'auto' }}>
        {presets.map((preset) => (
          <ListItem
            key={preset.id}
            disablePadding
            sx={{ mb: 0.5 }}
          >
            <ListItemButton
              selected={selectedId === preset.id}
              onClick={() => onSelect(preset.id)}
              sx={{
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <ListItemText
                primary={preset.name}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontFamily: 'Inter, sans-serif',
                  noWrap: true,
                  sx: { maxWidth: 140 },
                }}
              />
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Tooltip title="Duplicate">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(preset.id);
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <FileCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {presets.length > 1 && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(preset.id);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'action.hover',
                        '&:hover': { bgcolor: 'error.main', color: 'white' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export default function PresetsPage(props) {
  const { presets, addPreset, updatePreset, deletePreset, duplicatePreset, copyStageSettings } = useGrow();
  const [selectedPresetId, setSelectedPresetId] = React.useState(presets[0]?.id || null);
  const [copiedStage, setCopiedStage] = React.useState(null);

  const selectedPreset = presets.find((p) => p.id === selectedPresetId);

  const handleAddPreset = () => {
    const newId = addPreset('New Plant Preset');
    setSelectedPresetId(newId);
  };

  const handleDeletePreset = (presetId) => {
    deletePreset(presetId);
    if (selectedPresetId === presetId) {
      setSelectedPresetId(presets.find((p) => p.id !== presetId)?.id || null);
    }
  };

  const handleDuplicatePreset = (presetId) => {
    const newId = duplicatePreset(presetId);
    if (newId) {
      setSelectedPresetId(newId);
    }
  };

  const handleCopyStage = (stage) => {
    setCopiedStage(stage);
  };

  const handlePasteStage = (toStage) => {
    if (copiedStage && selectedPresetId) {
      copyStageSettings(selectedPresetId, copiedStage, toStage);
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
            height: '100vh',
          })}
        >
          <Stack
            sx={{
              mx: 3,
              pb: 5,
              pt: { xs: 10, md: 3 },
              height: '100%',
            }}
          >
            {/* Page header */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                mb: 3,
              }}
            >
              Grow Presets
            </Typography>

            {/* Main content area */}
            <Box sx={{ display: 'flex', gap: 3, flex: 1, minHeight: 0 }}>
              {/* Preset list sidebar */}
              <Card
                variant="outlined"
                sx={{
                  width: 280,
                  flexShrink: 0,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <PresetList
                  presets={presets}
                  selectedId={selectedPresetId}
                  onSelect={setSelectedPresetId}
                  onAdd={handleAddPreset}
                  onDelete={handleDeletePreset}
                  onDuplicate={handleDuplicatePreset}
                />
              </Card>

              {/* Preset editor */}
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {selectedPreset ? (
                  <PresetEditor
                    preset={selectedPreset}
                    onUpdate={updatePreset}
                    onCopyStage={handleCopyStage}
                    onPasteStage={handlePasteStage}
                    copiedStage={copiedStage}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Select a preset or create a new one
                    </Typography>
                  </Box>
                )}
              </Card>
            </Box>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
